import { ProgressBounds } from '../types/progress.types';

export const PROGRESS_ONGOING_MIN = 0.005;
export const PROGRESS_ONGOING_MAX = 0.995;
export const PROGRESS_NOT_STARTED_UPPER = 0.004;
export const PROGRESS_COMPLETED_VALUE = 1.0;

export type ProgressStatus = 'Not started' | 'Ongoing' | 'Completed';
export type ProgressStatusColor = 'red' | 'orange' | 'green';

const isProgressStatus = (value: string): value is ProgressStatus =>
    value === 'Not started' || value === 'Ongoing' || value === 'Completed';

export const getProgressRatio = (current?: number, total?: number): number | null => {
    if (typeof current !== 'number' || typeof total !== 'number') return null;
    if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 0) return null;
    return current / total;
};

const clampProgress = (progress: number): number => {
    if (!Number.isFinite(progress)) return 0;
    if (progress < 0) return 0;
    if (progress > PROGRESS_COMPLETED_VALUE) return PROGRESS_COMPLETED_VALUE;
    return progress;
};

export const getProgressStatus = (progress: number): ProgressStatus => {
    const value = clampProgress(progress);
    if (value >= PROGRESS_COMPLETED_VALUE) return 'Completed';
    if (value >= PROGRESS_ONGOING_MIN) return 'Ongoing';
    return 'Not started';
};

export const getProgressStatusColor = (progress: number): ProgressStatusColor => {
    const status = getProgressStatus(progress);
    if (status === 'Ongoing') return 'orange';
    if (status === 'Completed') return 'green';
    return 'red';
};

export const formatProgressPercent = (progress: number): string => {
    const value = clampProgress(progress);
    if (value >= PROGRESS_COMPLETED_VALUE) return '100%';
    return `${Math.min(99, Math.round(value * 100))}%`;
};

const PROGRESS_BOUNDS_BY_STATUS: Record<ProgressStatus, ProgressBounds> = {
    'Not started': { lower: -1.0, upper: PROGRESS_NOT_STARTED_UPPER },
    Ongoing: { lower: PROGRESS_ONGOING_MIN, upper: PROGRESS_ONGOING_MAX },
    Completed: { lower: PROGRESS_COMPLETED_VALUE, upper: PROGRESS_COMPLETED_VALUE },
};

export const toProgressBounds = (states: string[] | undefined): ProgressBounds[] => {
    if (!states || states.length === 0) return [];
    const unique = new Set(states.filter(isProgressStatus));
    return Array.from(unique).map((state) => PROGRESS_BOUNDS_BY_STATUS[state]);
};
